/**
 * Pagination Component
 *
 * Reusable pagination controls with page navigation and size selector.
 * Can be used across different tables and lists in the application.
 */

import React, { useMemo } from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  maxPagesToShow?: number;
  showRecordCount?: boolean;
  recordCountLabel?: string;
}

export function Pagination({
  currentPage,
  totalPages,
  totalRecords,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100, 200],
  maxPagesToShow = 5,
  showRecordCount = true,
  recordCountLabel,
}: PaginationProps) {
  // Calculate page numbers to display
  const pageNumbers = useMemo(() => {
    const pages: number[] = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    // Adjust start if we're near the end
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }, [currentPage, totalPages, maxPagesToShow]);

  // Calculate record range
  const recordStart = Math.min((currentPage - 1) * pageSize + 1, totalRecords);
  const recordEnd = Math.min(currentPage * pageSize, totalRecords);

  // Don't render if there's only one page and no records
  if (totalPages <= 1 && totalRecords === 0) {
    return null;
  }

  return (
    <div className="px-5 py-3 border-t border-gray-200 bg-gray-50">
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mb-3">
          {/* Navigation Buttons */}
          <div className="flex items-center gap-2">
            {/* First Page */}
            <button
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-gray-200 rounded-md text-base font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              aria-label="First page"
            >
              First
            </button>

            {/* Previous Page */}
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-gray-200 rounded-md text-base font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              aria-label="Previous page"
            >
              Previous
            </button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1" role="navigation" aria-label="Pagination">
              {pageNumbers.map(page => (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`px-3 py-1.5 border rounded-md text-base font-medium cursor-pointer transition-colors
                    ${page === currentPage
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'border-gray-200 hover:bg-gray-100'
                    }`}
                  aria-label={`Page ${page}`}
                  aria-current={page === currentPage ? 'page' : undefined}
                >
                  {page}
                </button>
              ))}
            </div>

            {/* Next Page */}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border border-gray-200 rounded-md text-base font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              aria-label="Next page"
            >
              Next
            </button>

            {/* Last Page */}
            <button
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border border-gray-200 rounded-md text-base font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              aria-label="Last page"
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* Bottom Row: Record Count + Page Size Selector */}
      <div className="flex justify-between items-center text-base text-gray-500">
        {/* Record Count */}
        {showRecordCount && totalRecords > 0 && (
          <div className="flex items-center gap-4">
            {recordCountLabel && <span>{recordCountLabel}</span>}
            <span>
              Showing {recordStart} to {recordEnd} of {totalRecords} records
            </span>
          </div>
        )}

        {/* Page Size Selector - Always visible on the right */}
        {onPageSizeChange && (
          <div className="flex items-center gap-2 ml-auto">
            <label htmlFor="page-size" className="text-base text-gray-600 font-medium">
              Rows per page:
            </label>
            <select
              id="page-size"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-base cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
              aria-label="Select page size"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
